import React, { useState, useEffect, useRef } from 'react';
import {
  DEFAULT_TIME_ZONE,
  formatZonedDisplay,
  utcToZonedTimeString
} from '../utils/timezone';

const timezonePattern = /([+-]\d{2}:\d{2}|Z)$/i;

const DateInput = ({
  name,
  value,
  onChange,
  required = false,
  min,
  max,
  className = '',
  timeZone = DEFAULT_TIME_ZONE
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const hiddenInputRef = useRef(null);
  const containerRef = useRef(null);
  const pickerTimeoutRef = useRef(null);

  // Format the date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return '';

    if (timezonePattern.test(dateValue)) {
      const zoned = utcToZonedTimeString(dateValue, timeZone);
      return formatZonedDisplay(zoned);
    }

    return formatZonedDisplay(dateValue);
  };

  const formatForPickerValue = (dateValue) => {
    if (!dateValue) return '';

    if (timezonePattern.test(dateValue)) {
      return utcToZonedTimeString(dateValue, timeZone);
    }

    return dateValue;
  };

  // Initialize display value when component mounts or value changes externally
  useEffect(() => {
    if (!isEditing) {
      const formatted = formatDate(value);
      setDisplayValue(formatted);
    }
  }, [value, isEditing]);

  useEffect(() => {
    return () => {
      if (pickerTimeoutRef.current) {
        clearTimeout(pickerTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    setIsEditing(true);
  };

  const handleInputFocus = () => {
    setIsEditing(true);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    
    // Try to parse the input value
    const dateTimePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/;
    const match = displayValue.match(dateTimePattern);

    if (match) {
      const [_, day, month, year, hours, minutes] = match;
      const pad = (num) => num.toString().padStart(2, '0');
      const zonedValue = `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${minutes}`;
      onChange({
        target: {
          name,
          value: zonedValue
        }
      });
      setDisplayValue(formatDate(zonedValue));
    } else {
      // Invalid format - revert to formatted value
      setDisplayValue(formatDate(value));
    }
  };

  const handleDateTimeChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue) {
      onChange({
        target: {
          name,
          value: selectedValue
        }
      });
      
      // Update display value
      setDisplayValue(formatDate(selectedValue));
      setIsEditing(false);
    }
    
    // Don't close picker immediately to allow month navigation
    pickerTimeoutRef.current = setTimeout(() => {
      setIsPickerOpen(false);
    }, 100);
  };

  const handleCalendarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isPickerOpen) {
      setIsPickerOpen(true);
      setTimeout(() => {
        if (hiddenInputRef.current) {
          try {
            hiddenInputRef.current.showPicker();
          } catch (error) {
            // Some browsers might not support showPicker
            hiddenInputRef.current.click();
          }
        }
      }, 0);
    }
  };

  const handleHiddenInputBlur = () => {
    // Delay closing to allow interaction with the picker
    pickerTimeoutRef.current = setTimeout(() => {
      setIsPickerOpen(false);
    }, 200);
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        name={name}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder="dd/mm/yyyy hh:mm"
        required={required}
        className={className}
        title="Please enter date in format: dd/mm/yyyy hh:mm"
      />
      <button
        type="button"
        onClick={handleCalendarClick}
        className="absolute inset-y-0 right-0 pr-3 flex items-center"
      >
        <svg className="w-5 h-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>
      <input
        ref={hiddenInputRef}
        type="datetime-local"
        value={formatForPickerValue(value) || ''}
        onChange={handleDateTimeChange}
        onBlur={handleHiddenInputBlur}
        min={formatForPickerValue(min) || undefined}
        max={formatForPickerValue(max) || undefined}
        className="absolute top-full left-0 mt-1 opacity-0"
        style={{ 
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '4px',
          opacity: isPickerOpen ? 0.01 : 0,
          pointerEvents: isPickerOpen ? 'auto' : 'none',
          zIndex: 9999
        }}
        tabIndex={isPickerOpen ? 0 : -1}
      />
    </div>
  );
};

export default DateInput;
